import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useState, useMemo, MouseEvent, KeyboardEvent } from 'react';

import { ChatSettings } from 'pages/maxi-ai/sidebar/ChatSettings';

import { Search } from 'components/Search';
import { Input } from 'components/data-entry/Input';
import { Button } from 'components/general/Button';

import { useThreads } from 'hooks/maxi-ai/useThreads';

import { Logger } from 'utils/logger';

import { useMaxiAIStore } from 'store/maxi-ai.store';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatSidebar({ isOpen, onClose }: ChatSidebarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const { threadId, setThreadId, setMessages } = useMaxiAIStore();
  const {
    threads,
    isLoading: isLoadingThreads,
    selectThread,
    updateThreadTitle,
    updateThreadPinned,
    deleteThread,
  } = useThreads();

  // Filter and sort threads based on search value and pinned status
  const filteredThreads = useMemo(() => {
    // Clone before sorting to avoid mutating store state
    let filtered = [...threads];

    if (searchValue) {
      filtered = threads.filter((thread) =>
        thread.title.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Sort threads: pinned threads first, then by creation date (newest first)
    return filtered.sort((a, b) => {
      // First sort by pinned status
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // Then sort by creation date (newest first)
      const dateA = a.created_at ? dayjs(a.created_at) : dayjs(0);
      const dateB = b.created_at ? dayjs(b.created_at) : dayjs(0);
      return dateB.valueOf() - dateA.valueOf();
    });
  }, [threads, searchValue]);

  // Handle thread selection
  const handleThreadSelect = async (selectedThreadId: string) => {
    try {
      setLoadingThreadId(selectedThreadId);
      await selectThread(selectedThreadId);
      onClose();
    } catch (error) {
      Logger.error('Failed to select thread:', error);
      // Could add error handling UI here
    } finally {
      setLoadingThreadId(null);
    }
  };

  // Handle starting a new chat
  const handleNewChat = () => {
    setThreadId(null);
    setMessages([]);
    onClose();
  };

  // Handle ellipses click
  const handleEllipsesClick = (e: MouseEvent, threadId: string) => {
    e.stopPropagation(); // Prevent thread selection
    setOpenPopoverId(openPopoverId === threadId ? null : threadId);
  };

  // Handle rename thread
  const handleRenameThread = (threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (thread) {
      setRenamingThreadId(threadId);
      setRenameValue(thread.title);
      setOpenPopoverId(null);
    }
  };

  // Handle rename save
  const handleRenameSave = async () => {
    if (!renamingThreadId || !renameValue.trim()) {
      setRenamingThreadId(null);
      setRenameValue('');
      return;
    }

    try {
      await updateThreadTitle(renamingThreadId, renameValue.trim());
      setRenamingThreadId(null);
      setRenameValue('');
    } catch (error) {
      Logger.error('Failed to update thread title:', error);
      // Keep the input open on error so user can try again
    }
  };

  // Handle rename cancel
  const handleRenameCancel = () => {
    setRenamingThreadId(null);
    setRenameValue('');
  };

  // Handle rename input key press
  const handleRenameKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSave();
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  // Handle pin thread
  const handlePinThread = async (threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (!thread) return;

    try {
      await updateThreadPinned(threadId, !thread.pinned);
      setOpenPopoverId(null);
    } catch (error) {
      Logger.error('Failed to pin/unpin thread:', error);
      setOpenPopoverId(null);
    }
  };

  // Handle delete thread
  const handleDeleteThread = async (threadId: string) => {
    try {
      await deleteThread(threadId);
      setOpenPopoverId(null);
    } catch (error) {
      Logger.error('Failed to delete thread:', error);
      setOpenPopoverId(null);
    }
  };

  return (
    <div
      className={`absolute top-2 left-2 bottom-2 bg-white rounded-lg border border-color-border shadow-lg z-[1000] w-60 transition-all duration-300 ease-in-out ${
        isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
    >
      <div className="p-2 h-full flex flex-col gap-3">
        <div className="flex items-center justify-between px-2 pt-2 pb-1">
          <h3 className="font-medium text-sm">All Chats</h3>
          <Icon
            icon="ant-design:menu-fold-outlined"
            className="text-sm cursor-pointer"
            onClick={onClose}
          />
        </div>

        <div className="px-2 flex flex-col items-center gap-2">
          <Search
            placeholder="Search chats"
            value={searchValue}
            onChange={setSearchValue}
          />
          <Button className="w-[200px] h-8 shadow-md" onClick={handleNewChat}>
            <div className="w-full flex justify-center items-center gap-2">
              <Icon icon="mdi:plus" className="w-4 h-4" />
              <div className="text-sm">New chat</div>
            </div>
          </Button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="text-sm text-color-text-secondary font-medium mb-2 mx-2">
            Chat history
          </div>

          <div className="flex flex-col overflow-y-auto flex-1">
            {isLoadingThreads ? (
              <div className="text-sm text-color-text-secondary p-2">
                Loading threads...
              </div>
            ) : filteredThreads.length > 0 ? (
              filteredThreads.map((thread) => (
                <div key={`thread-${thread.id}`} className="relative">
                  <Button
                    type="text"
                    onClick={() =>
                      renamingThreadId !== thread.id &&
                      handleThreadSelect(thread.id)
                    }
                    disabled={
                      loadingThreadId === thread.id ||
                      renamingThreadId === thread.id
                    }
                    className={`w-full justify-between pl-2 pr-1 hover:border-transparent group ${
                      threadId === thread.id
                        ? 'bg-color-fill-content'
                        : `bg-color-white ${!thread.pinned && 'text-color-text-secondary'}`
                    } ${loadingThreadId === thread.id ? 'opacity-50' : ''}`}
                  >
                    <div className="truncate text-left flex-1 min-w-0">
                      {loadingThreadId === thread.id ? (
                        'Loading...'
                      ) : renamingThreadId === thread.id ? (
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="text-sm"
                          autoFocus={true}
                          onKeyDown={handleRenameKeyPress}
                          onBlur={handleRenameSave}
                          style={{
                            border: 'none',
                            boxShadow: 'none',
                            padding: '0',
                            background: 'transparent',
                            fontSize: '14px',
                          }}
                        />
                      ) : (
                        <span className="truncate">{thread.title}</span>
                      )}
                    </div>
                    {renamingThreadId !== thread.id && (
                      <div className="flex-shrink-0 ml-1">
                        {thread.pinned ? (
                          // Show pin icon for pinned threads, with ellipsis on hover
                          <ChatSettings
                            threadId={thread.id}
                            isOpen={openPopoverId === thread.id}
                            onOpenChange={(open) =>
                              setOpenPopoverId(open ? thread.id : null)
                            }
                            onPin={handlePinThread}
                            onRename={handleRenameThread}
                            onDelete={handleDeleteThread}
                            isPinned={thread.pinned}
                          >
                            <div className="relative">
                              <Icon
                                icon="ant-design:pushpin-outlined"
                                className="w-4 h-4 text-color-primary cursor-pointer group-hover:opacity-0 transition-opacity duration-200"
                                onClick={(e) =>
                                  handleEllipsesClick(e, thread.id)
                                }
                              />
                              <Icon
                                icon="ant-design:ellipsis-outlined"
                                className="w-4 h-4 absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-color-text cursor-pointer"
                                onClick={(e) =>
                                  handleEllipsesClick(e, thread.id)
                                }
                              />
                            </div>
                          </ChatSettings>
                        ) : (
                          // Show ellipsis for unpinned threads
                          <ChatSettings
                            threadId={thread.id}
                            isOpen={openPopoverId === thread.id}
                            onOpenChange={(open) =>
                              setOpenPopoverId(open ? thread.id : null)
                            }
                            onPin={handlePinThread}
                            onRename={handleRenameThread}
                            onDelete={handleDeleteThread}
                            isPinned={thread.pinned}
                          >
                            <Icon
                              icon="ant-design:ellipsis-outlined"
                              className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-color-text cursor-pointer"
                              onClick={(e) => handleEllipsesClick(e, thread.id)}
                            />
                          </ChatSettings>
                        )}
                      </div>
                    )}
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-sm text-color-text-secondary p-2">
                {searchValue ? 'No chats found' : 'No chats available'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
